import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { parseValidHttpsUrl } from 'common/parse-valid-https-url';
import {
  clearStaleBookingIframeSession,
  persistBookingIframeSession,
  readStoredBookingIframeSession,
} from 'utils/book-appointment-location-state';

export type UseBookingModalVisibilityResult = {
  showBookingModal: boolean;
  bookingLink: string | undefined;
};

/**
 * Resolves whether the booking iframe modal should be shown and with which
 * link. The link comes from router state (masonry action) or, as a fallback,
 * from tab {@link sessionStorage} keyed to the current route so the modal
 * survives a refresh. Keeps the stored session in sync with the active route.
 */
export function useBookingModalVisibility(): UseBookingModalVisibilityResult {
  const { pathname, search, state } = useLocation();
  const currentReturnPath = `${pathname}${search}`;

  const bookingLinkFromLocation = parseValidHttpsUrl(
    typeof state === 'object' && state !== null && 'bookingLink' in state
      ? (state as { bookingLink?: unknown }).bookingLink
      : undefined,
  );

  const bookingLinkFromSession =
    readStoredBookingIframeSession(currentReturnPath);

  const bookingLink = bookingLinkFromLocation ?? bookingLinkFromSession;

  useEffect(() => {
    if (bookingLinkFromLocation) {
      persistBookingIframeSession(bookingLinkFromLocation, currentReturnPath);
    }
  }, [bookingLinkFromLocation, currentReturnPath]);

  useEffect(() => {
    clearStaleBookingIframeSession(currentReturnPath);
  }, [currentReturnPath]);

  return {
    showBookingModal: bookingLink !== undefined,
    bookingLink,
  };
}
